<script setup lang="ts">
import { ref, computed } from 'vue'
import { useVehicleStore } from '../../stores/vehicleStore'

const vehicleStore = useVehicleStore()
const showDetails = ref(false)

const emit = defineEmits<{
  changeVehicle: []
}>()

function formatYear(raw: number): string {
  if (!raw || raw === 0) return ''
  return String(Math.floor(raw / 100))
}

const carName = computed(() => {
  if (!vehicleStore.carDetails) return ''
  const d = vehicleStore.carDetails
  return [d.manuName, d.modelName, d.typeName].filter(Boolean).join(' ')
})

const svv = computed(() => vehicleStore.svvDetails)
const svvCar = computed(() => {
  if (!svv.value?.kjoretoydataListe?.length) return null
  return svv.value.kjoretoydataListe[0]
})
</script>

<template>
  <div v-if="vehicleStore.carDetails" class="vehicle-info">
    <div class="vehicle-info__bar">
      <div class="vehicle-info__name">
        <strong>{{ carName }}</strong>
      </div>
      <div class="vehicle-info__actions">
        <a href="#" class="vehicle-info__link" @click.prevent="showDetails = !showDetails">
          Se detaljer
        </a>
        <a href="#" class="vehicle-info__link" @click.prevent="emit('changeVehicle')">
          Endre kjøretøy
        </a>
      </div>
    </div>

    <!-- Expandable detail panel -->
    <transition name="slide">
      <div v-if="showDetails" class="vehicle-info__details">
        <!-- TecDoc motor details -->
        <div class="vehicle-info__section">
          <div class="vehicle-info__row">
            <span class="vehicle-info__label">Produsent</span>
            <span class="vehicle-info__value">{{ vehicleStore.carDetails.manuName }}</span>
          </div>
          <div class="vehicle-info__row">
            <span class="vehicle-info__label">Modellserie</span>
            <span class="vehicle-info__value">{{ vehicleStore.carDetails.modelName }}</span>
          </div>
          <div class="vehicle-info__row">
            <span class="vehicle-info__label">Type</span>
            <span class="vehicle-info__value">{{ vehicleStore.carDetails.typeName }}</span>
          </div>
          <div v-if="vehicleStore.carDetails.impulsionType" class="vehicle-info__row">
            <span class="vehicle-info__label">Drivhjul</span>
            <span class="vehicle-info__value">{{ vehicleStore.carDetails.impulsionType }}</span>
          </div>
          <div v-if="vehicleStore.regNr" class="vehicle-info__row">
            <span class="vehicle-info__label">Reg.nr</span>
            <span class="vehicle-info__value">{{ vehicleStore.regNr.toUpperCase() }}</span>
          </div>
          <div class="vehicle-info__row">
            <span class="vehicle-info__label">Produksjonsår</span>
            <span class="vehicle-info__value">
              År: {{ formatYear(vehicleStore.carDetails.yearFrom) }} - År: {{ formatYear(vehicleStore.carDetails.yearTo) }}
            </span>
          </div>
          <div class="vehicle-info__row">
            <span class="vehicle-info__label">Kw / Hp</span>
            <span class="vehicle-info__value">
              {{ vehicleStore.carDetails.powerKw }} KW - {{ vehicleStore.carDetails.powerKwTo }} KW /
              {{ vehicleStore.carDetails.powerHp }} HP - {{ vehicleStore.carDetails.powerHpTo }} HP
            </span>
          </div>
          <div class="vehicle-info__row">
            <span class="vehicle-info__label">Ccm</span>
            <span class="vehicle-info__value">{{ vehicleStore.carDetails.cylinderCapacity }}</span>
          </div>
          <div v-if="vehicleStore.carDetails.motorCode" class="vehicle-info__row">
            <span class="vehicle-info__label">Motorkode</span>
            <span class="vehicle-info__value">{{ vehicleStore.carDetails.motorCode }}</span>
          </div>
        </div>

        <!-- SVV details (if available) -->
        <div v-if="svvCar" class="vehicle-info__section">
          <div v-if="svvCar.kjoretoyId?.understellsnummer" class="vehicle-info__row">
            <span class="vehicle-info__label">Understellsnr.</span>
            <span class="vehicle-info__value">{{ svvCar.kjoretoyId.understellsnummer }}</span>
          </div>
          <div v-if="svvCar.godkjenning?.forstegangsGodkjenning?.forstegangRegistrertDato" class="vehicle-info__row">
            <span class="vehicle-info__label">Førstegang reg.år</span>
            <span class="vehicle-info__value">{{ svvCar.godkjenning.forstegangsGodkjenning.forstegangRegistrertDato }}</span>
          </div>
          <div v-if="svvCar.forstegangsregistrering?.registrertForstegangNorgeDato" class="vehicle-info__row">
            <span class="vehicle-info__label">Førstegang reg. Norge</span>
            <span class="vehicle-info__value">{{ svvCar.forstegangsregistrering.registrertForstegangNorgeDato }}</span>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<style scoped lang="scss">
.vehicle-info {
  margin-bottom: 1rem;
  max-width: 500px;

  &__bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    padding: 0.75rem 1rem;
    background: #f0f4f8;
    border-radius: 8px;
    flex-wrap: wrap;
  }

  @media (max-width: 768px) {
    max-width: 100%;
  }

  &__name {
    font-size: 1.05rem;
  }

  &__actions {
    display: flex;
    gap: 1rem;
  }

  &__link {
    color: #002868;
    text-decoration: underline;
    cursor: pointer;
    font-size: 0.9rem;

    &:hover { color: #001d4f; }
  }

  &__details {
    background: white;
    border: 1px solid #e5e7eb;
    border-top: none;
    border-radius: 0 0 8px 8px;
    padding: 1rem;
    overflow: hidden;
  }

  &__section {
    &:not(:last-child) {
      border-bottom: 1px solid #f3f4f6;
      padding-bottom: 0.75rem;
      margin-bottom: 0.75rem;
    }
  }

  &__row {
    display: flex;
    gap: 1rem;
    padding: 3px 0;
    font-size: 0.85rem;
  }

  &__label {
    color: #666;
    min-width: 140px;
    flex-shrink: 0;
  }

  &__value {
    font-weight: 500;
  }
}

.slide-enter-active,
.slide-leave-active {
  transition: max-height 0.2s ease, opacity 0.2s ease;
}

.slide-enter-from,
.slide-leave-to {
  max-height: 0;
  opacity: 0;
}

.slide-enter-to,
.slide-leave-from {
  max-height: 500px;
  opacity: 1;
}
</style>
